import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Radio,
  Select,
  Button,
  Form,
  DatePicker,
  Checkbox,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import AlertBox from '@/components/alert-box';
import { Dialog } from '@/components/dialog/Dialog';
import UploadFiles from '@/components/upload/UploadFiles';
import { useQueryParams, useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  isNonEmploymentTypeValue,
} from '@/shared/employee';
import { useSalaryStore } from '@/store/useSalaryStore';
import { DATE_FORMATS } from '@/utils/formUtils';
import { getUserFullName } from '@/utils/utils';

/**
 * `preset` ({ employeeId, companyId }) opens the modal for one known employee,
 * as the employee edit page does for an expired or archived contract. Such
 * profiles are only offered behind "show all terminated", so that starts on.
 */
export default function ExtendEmploymentModal({ metadata, preset }) {
  const presetEmployeeId = preset?.employeeId;
  const presetCompanyId = preset?.companyId;

  // `messages` has to be named here, not only at the call site: the two rehire
  // warnings read from it, and an undeclared namespace is not waited for, so
  // they rendered as their raw keys.
  const { t } = useTranslation(['forms', 'tables', 'menus', 'messages']);
  const notification = useNotify();
  const queryClient = useQueryClient();
  const {
    queryParams: { company },
  } = useQueryParams();

  const [form] = Form.useForm();

  const getCompanyIdFromQueryParams = useCallback(() => {
    return company && company !== 'all' ? company : undefined;
  }, [company]);

  const initialValues = useMemo(() => {
    return {
      employmentType: 'unlimited',
      company: presetCompanyId || getCompanyIdFromQueryParams(),
      effectiveDate: dayjs(),
    };
  }, [getCompanyIdFromQueryParams, presetCompanyId]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const [toggle, setToggle] = useState(false);
  const [includeAllTerminated, setIncludeAllTerminated] = useState(
    !!presetEmployeeId
  );

  const { isDirty } = useSalaryStore(
    useShallow((state) => ({
      isDirty: state.isDirty,
    }))
  );

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleUploadedFiles = useCallback(({ action: { type, payload } }) => {
    switch (type) {
      case 'upload':
        setUploadedFiles((prev) => {
          const exists = prev.some((f) => f.uid === payload.uid);
          return exists ? prev : [...prev, payload];
        });
        break;
      case 'remove':
        setUploadedFiles((prev) => prev.filter((file) => file.uid !== payload));
        break;
      case 'reset': {
        setUploadedFiles([]);
        break;
      }
      default:
        break;
    }
  }, []);

  const { data: companyOptions, isLoading: companyIsLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesDropdown],
    queryFn: async () => {
      return await API.companies.retrieveDropdownAllCompanies(null);
    },
    select: (data) => {
      const companies = data?.items || [];

      return companies?.map((company) => ({
        label: company.name,
        value: company._id,
      }));
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isSelectedCompany = Form.useWatch('company', form);
  const isSelectedLimited = Form.useWatch('employmentType', form) === 'limited';

  useEffect(() => {
    form.setFieldsValue({ employee: undefined });
  }, [form, isSelectedCompany]);

  const { data: employeeOptionsData, isLoading: employeeOptionsIsLoading } =
    useQuery({
      queryKey: [
        QUERY_KEY.employeesDropdown,
        isSelectedCompany,
        includeAllTerminated,
      ],
      queryFn: async () =>
        await API.employees.retrieveEmployeeEmploymentEmployees({
          companyId: isSelectedCompany,
          type: 'extension',
          includeAllTerminated,
        }),
      select: (data) => {
        return data;
      },
      enabled: !!isSelectedCompany,
    });

  const employeeOptions = useMemo(() => {
    return employeeOptionsData?.items?.length
      ? employeeOptionsData?.items?.map((emp) => ({
          value: emp._id,
          label: getUserFullName(emp.firstName, emp.lastName),
          status: emp.status,
          endDate: emp.employmentSettings?.endDate,
          employmentType: emp.employmentSettings?.employmentType,
        }))
      : [];
  }, [employeeOptionsData?.items]);

  // Track selected employee's status
  const selectedEmployeeId = Form.useWatch('employee', form);

  // The company effect above clears `employee` whenever the company is set —
  // which can land after this runs — so the preset employee is put back for as
  // long as the field is empty and the options list holds him.
  useEffect(() => {
    if (!toggle || !presetEmployeeId || selectedEmployeeId) return;
    if (employeeOptions.some((emp) => emp.value === presetEmployeeId)) {
      form.setFieldsValue({ employee: presetEmployeeId });
    }
  }, [toggle, presetEmployeeId, selectedEmployeeId, employeeOptions, form]);
  const selectedEmployee = useMemo(() => {
    return employeeOptions.find((emp) => emp.value === selectedEmployeeId);
  }, [employeeOptions, selectedEmployeeId]);

  const isSelectedEmployeeTerminated = selectedEmployee?.status === 'TERMINATED';

  // A limited contract that ran out keeps status ACTIVE, but it is over just
  // the same: the backend treats it like a termination and needs the new start
  // date to tell a continuation from a rehire after a gap.
  const isSelectedContractExpired =
    selectedEmployee?.employmentType === 'limited' &&
    !!selectedEmployee?.endDate &&
    dayjs(selectedEmployee.endDate).isBefore(dayjs(), 'day');

  const needsRehireStartDate =
    isSelectedEmployeeTerminated || isSelectedContractExpired;

  // Converting a non-employment worker (service contract / author / PP) into a
  // regular employment. The annual-leave balance is derived automatically on the
  // backend from the worker's absence record; HR only sets the employment start
  // date (which decides whether the contract period counts toward seniority).
  const isConvertingFromNonEmployment = isNonEmploymentTypeValue(
    selectedEmployee?.employmentType
  );

  useEffect(() => {
    // Default the employment start to today (contract period not counted); HR can
    // backdate it. Only seed for an active conversion — the gap-rehire path for
    // terminated workers manages its own start date.
    if (isConvertingFromNonEmployment && !needsRehireStartDate) {
      form.setFieldsValue({ startDate: dayjs() });
    }
  }, [isConvertingFromNonEmployment, needsRehireStartDate, form]);

  const handleCancelModal = () => {
    setToggle(false);
  };

  const onReset = () => {
    form.resetFields();
    setIncludeAllTerminated(!!presetEmployeeId);
    handleCancelModal();
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (formValues) => {
      const payload = { ...formValues, type: 'extension' };

      // Convert date fields to YYYY-MM-DD format to avoid timezone issues
      if (payload.endDate) {
        payload.endDate = payload.endDate.format('YYYY-MM-DD');
      }
      if (payload.startDate) {
        payload.startDate = payload.startDate.format('YYYY-MM-DD');
      }
      if (payload.effectiveDate) {
        payload.effectiveDate = payload.effectiveDate.format('YYYY-MM-DD');
      }

      const formDataToSend = new FormData();
      for (const [key, value] of Object.entries(payload)) {
        formDataToSend.append(key, JSON.stringify(value));
      }

      uploadedFiles.forEach((file) => {
        formDataToSend.append('files', file.originFileObj);
        formDataToSend.append('createExtensionDoc', true);
      });

      return await API.employees.updateEmploymentStatus(formDataToSend);
    },
    onSuccess: (response) => {
      notification.success({ message: response.message });
      queryClient.invalidateQueries([QUERY_KEY.employees]);
      if (presetEmployeeId) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.employee] });
      }
      onReset();
      handleUploadedFiles({ action: { type: 'reset' } });
    },
    onError: (error) => {
      console.error('Failed to submit change of employment:', error);
      notification.error({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to submit change of employment',
      });
    },
  });

  const onFinish = async (values) => {
    await mutateAsync(values);
  };

  const dotButtonAnimation = (dotColor) => {
    return (
      <span
        className={cn(
          'block h-2 w-2 shrink-0 rounded-full',
          dotColor ? dotColor : 'bg-zinc-100'
        )}
      >
        <span
          className={cn(
            'block h-2 w-2 shrink-0 rounded-full group-hover:animate-ping',
            dotColor ? dotColor : 'bg-zinc-100'
          )}
        />
      </span>
    );
  };

  return (
    <Dialog
      form={form}
      onSubmit={onFinish}
      title={t('extendEmployment', { ns: 'menus' })}
      open={toggle}
      className="!h-auto !w-[768px]"
      onCancel={handleCancelModal}
      confirmLoading={isPending}
      destroyOnClose
      destroyOnHidden
      triggerChildren={
        metadata?.showDot ? (
          <Button
            title={t('extendEmployment', { ns: 'menus' })}
            type="default"
            className={cn(
              'btn-default group relative justify-start px-0',
              metadata?.buttonClassName
            )}
            onClick={() => setToggle(true)}
            disabled={isDirty}
          >
            {dotButtonAnimation('bg-secondary')}
            <span className="mr-2">
              {t('extendEmployment', { ns: 'menus' })}
            </span>
          </Button>
        ) : (
          <Button
            title={t('extendEmployment', { ns: 'menus' })}
            type="default"
            className={cn(
              'btn-default btn-default-active h-8 bg-secondary text-black hover:!bg-secondary',
              metadata?.buttonClassName
            )}
            icon={<ClockCircleOutlined />}
            onClick={() => setToggle(true)}
            disabled={isDirty}
          >
            <span className="mr-2">
              {t('extendEmployment', { ns: 'menus' })}
            </span>
          </Button>
        )
      }
    >
      <div className="-mx-4 -my-6 grid max-h-[calc(100svh_-_298px)] gap-4 overflow-y-auto px-4 py-6">
        <Form
          form={form}
          name="extend-employment-form"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          className={cn('form')}
          initialValues={initialValues}
        >
          <AlertBox variant="info" className="relative p-2">
            <span>{t('extendEmploymentInfoMessage', { ns: 'messages' })}</span>
          </AlertBox>

          <Form.Item
            label={t('selectCompany', { ns: 'forms' })}
            name="company"
            rules={[
              {
                required: true,
                message: t('formValidation.companyRequired', {
                  ns: 'forms',
                }),
              },
            ]}
          >
            <Select
              disabled={!!presetEmployeeId}
              allowClear
              showSearch
              mode="single"
              placeholder={t('selectCompany', { ns: 'forms' })}
              className="w-full"
              optionFilterProp="label"
              loading={companyIsLoading}
              options={companyOptions}
            />
          </Form.Item>

          {isSelectedCompany && (
            <div className="mb-4">
              <Checkbox
                disabled={!!presetEmployeeId}
                checked={includeAllTerminated}
                onChange={(e) => setIncludeAllTerminated(e.target.checked)}
              >
                {t('showAllTerminatedEmployees', { ns: 'forms' })}
              </Checkbox>
            </div>
          )}

          <Form.Item
            label={t('selectEmployee', { ns: 'forms' })}
            name="employee"
            rules={[
              {
                required: true,
                message: t('formValidation.companyRequired', {
                  ns: 'forms',
                }),
              },
            ]}
          >
            <Select
              disabled={!isSelectedCompany || !!presetEmployeeId}
              allowClear
              showSearch
              mode="single"
              placeholder={t('selectEmployee', { ns: 'forms' })}
              className="w-full"
              optionFilterProp="label"
              loading={employeeOptionsIsLoading}
              options={employeeOptions}
            />
          </Form.Item>

          {isConvertingFromNonEmployment && !needsRehireStartDate && (
            <>
              <AlertBox variant="warning" className="relative mb-4 p-2">
                <span>
                  {t('convertNonEmploymentToRegularWarning', {
                    ns: 'messages',
                    defaultValue:
                      'Ovaj radnik je van radnog odnosa i biće prebačen u radni odnos (izaberite tip i, ako je na određeno, datum prestanka). Godišnji odmor se automatski preuzima iz evidencije odsustva.',
                  })}
                </span>
              </AlertBox>

              <Form.Item
                name="startDate"
                label={
                  <span className="inline-flex items-center gap-1">
                    {t('startOfWork', { ns: 'forms' })}
                    <Tooltip
                      title={t('conversionStartDateHelp', {
                        ns: 'forms',
                        defaultValue:
                          'Od ovog datuma počinje radni odnos — računaju se staž i godišnji odmor. Period rada po ugovoru pre ovog datuma se NE uračunava; unesite raniji datum ako želite da se uračuna.',
                      })}
                    >
                      <InfoCircleOutlined className="text-xs opacity-60" />
                    </Tooltip>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: t('formValidation.selectDateRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <DatePicker
                  className="w-full"
                  format={DATE_FORMATS.DISPLAY}
                  placeholder={t('startOfWork', { ns: 'forms' })}
                />
              </Form.Item>
            </>
          )}

          {needsRehireStartDate && (
            <>
              <AlertBox variant="warning" className="relative mb-4 p-2">
                <span>
                  {isSelectedEmployeeTerminated
                    ? t('gapRehireWarningMessage', { ns: 'messages' })
                    : t('contractExpiredRehireWarningMessage', {
                        ns: 'messages',
                        date: dayjs(selectedEmployee.endDate).format(
                          DATE_FORMATS.DISPLAY
                        ),
                      })}
                </span>
              </AlertBox>

              <Form.Item
                name="startDate"
                label={t('startOfWork', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('formValidation.selectDateRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <DatePicker
                  disabled={!isSelectedCompany}
                  className="w-full"
                  format={DATE_FORMATS.DISPLAY}
                  placeholder={t('startOfWork', { ns: 'forms' })}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="employmentType"
            label={t('employmentType', { ns: 'forms' })}
            className="!mb-5"
          >
            <Radio.Group className="grid gap-4 sm:flex [&_label]:items-start [&_label]:justify-start">
              {EMPLOYMENT_TYPE_OPTIONS.map(({ key, value }) => (
                <Radio
                  key={key}
                  value={value}
                  className="mr-0 w-full rounded border border-zinc-200 bg-white px-2 py-1 hover:border-accent"
                >
                  {t(key, { ns: 'forms' })}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          {isSelectedLimited ? (
            <Form.Item
              name="endDate"
              label={t('endOfWork', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.selectDateRequired', {
                    ns: 'forms',
                  }),
                },
              ]}
            >
              <DatePicker
                disabled={!isSelectedCompany}
                className="w-full"
                format={DATE_FORMATS.DISPLAY}
                placeholder={t('endOfWork', { ns: 'forms' })}
              />
            </Form.Item>
          ) : null}

          <Form.Item
            name="effectiveDate"
            label={
              <span className="inline-flex items-center gap-1">
                {t('effectiveDate', { ns: 'forms' })}
                <Tooltip title={t('effectiveDateHelp', { ns: 'forms' })}>
                  <InfoCircleOutlined className="text-xs opacity-60" />
                </Tooltip>
              </span>
            }
          >
            <DatePicker
              className="w-full"
              format={DATE_FORMATS.DISPLAY}
              placeholder={t('effectiveDate', { ns: 'forms' })}
            />
          </Form.Item>

          <UploadFiles
            uploadName="extendEmployment"
            fileList={uploadedFiles}
            onUploaded={handleUploadedFiles}
          />
        </Form>
      </div>
    </Dialog>
  );
}
